/**
 * PR-AdminUsers-6 — /admin/users
 * Lista de usuarios con búsqueda, filtros y creación inline.
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { UserPlus, Search } from "lucide-react";
import { ApiError } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAdminUsers, useCreateUser } from "@/hooks/useAdminUsers";
import { cn } from "@/lib/utils";

type Filter = "all" | "active" | "inactive";

export function AdminUsersPage() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState<Filter>("all");
  const [showNew, setShowNew] = useState(false);
  const [newUsername, setNewUsername] = useState("");
  const [newRole, setNewRole] = useState("operator");
  const [tempPassword, setTempPassword] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const isActive = filter === "all" ? undefined : filter === "active";
  const users = useAdminUsers({ q: q || undefined, is_active: isActive });
  const createUser = useCreateUser();

  const handleCreate = async () => {
    if (!newUsername.trim()) return;
    setError(null);
    try {
      const res = await createUser.mutateAsync({ username: newUsername.trim(), role: newRole });
      setTempPassword(res.temp_password);
      setNewUsername("");
    } catch (e) {
      if (e instanceof ApiError) setError(e.message);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Usuarios</h1>
          <p className="text-sm text-muted-foreground">Gestión de usuarios y permisos.</p>
        </div>
        <Button onClick={() => { setShowNew(true); setTempPassword(null); setError(null); }}>
          <UserPlus className="size-4" /> Nuevo usuario
        </Button>
      </div>

      {/* Formulario nuevo usuario */}
      {showNew && (
        <div className="rounded-lg border bg-card p-4 space-y-3">
          <p className="text-sm font-medium">Nuevo usuario</p>
          {tempPassword ? (
            <div className="space-y-2">
              <p className="text-sm text-emerald-600 dark:text-emerald-400">
                Usuario creado. Password temporal (solo visible una vez):
              </p>
              <code className="block rounded bg-muted px-3 py-2 font-mono text-sm select-all">
                {tempPassword}
              </code>
              <Button variant="ghost" size="sm" onClick={() => { setShowNew(false); setTempPassword(null); }}>
                Cerrar
              </Button>
            </div>
          ) : (
            <div className="flex items-end gap-3">
              <div className="flex-1">
                <label className="text-xs text-muted-foreground mb-1 block">Username</label>
                <Input
                  value={newUsername}
                  onChange={e => setNewUsername(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && void handleCreate()}
                  placeholder="nombre.usuario"
                />
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Rol</label>
                <select
                  value={newRole}
                  onChange={e => setNewRole(e.target.value)}
                  className="rounded-md border bg-background px-3 py-2 text-sm"
                >
                  <option value="operator">Operador</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <Button onClick={() => void handleCreate()} disabled={createUser.isPending}>
                Crear
              </Button>
              <Button variant="ghost" onClick={() => setShowNew(false)}>
                Cancelar
              </Button>
            </div>
          )}
          {error && <p className="text-xs text-destructive">{error}</p>}
        </div>
      )}

      {/* Filtros */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-2.5 size-4 text-muted-foreground" />
          <Input
            className="pl-9"
            placeholder="Buscar por username…"
            value={q}
            onChange={e => setQ(e.target.value)}
          />
        </div>
        {(["all", "active", "inactive"] as Filter[]).map(f => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={cn(
              "rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
              filter === f
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground hover:bg-accent",
            )}
          >
            {f === "all" ? "Todos" : f === "active" ? "Activos" : "Inactivos"}
          </button>
        ))}
      </div>

      {/* Tabla */}
      {users.isPending && <p className="text-sm text-muted-foreground">Cargando…</p>}
      {users.isError && <p className="text-sm text-destructive">Error al cargar usuarios.</p>}
      {users.data && (
        <div className="rounded-lg border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                <th className="px-4 py-3 text-left font-medium">Username</th>
                <th className="px-4 py-3 text-left font-medium">Rol</th>
                <th className="px-4 py-3 text-left font-medium">Activo</th>
                <th className="px-4 py-3 text-left font-medium">2FA</th>
                <th className="px-4 py-3 text-left font-medium">Segmentos</th>
                <th className="px-4 py-3 text-left font-medium">Áreas</th>
                <th className="px-4 py-3 text-left font-medium">Roles globales</th>
              </tr>
            </thead>
            <tbody>
              {users.data.items.map(u => (
                <tr
                  key={u.id}
                  onClick={() => navigate(`/admin/users/${u.id}`)}
                  className="border-t cursor-pointer hover:bg-accent/50 transition-colors"
                >
                  <td className="px-4 py-3 font-medium">{u.username}</td>
                  <td className="px-4 py-3 text-muted-foreground">{u.role}</td>
                  <td className="px-4 py-3">
                    <span className={cn(
                      "rounded-full px-2 py-0.5 text-[10px] font-semibold",
                      u.is_active
                        ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400"
                        : "bg-red-500/15 text-red-700 dark:text-red-400",
                    )}>
                      {u.is_active ? "Sí" : "No"}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={cn(
                      "rounded-full px-2 py-0.5 text-[10px] font-semibold",
                      u.has_2fa
                        ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400"
                        : "bg-muted text-muted-foreground",
                    )}>
                      {u.has_2fa ? "Activo" : "No"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">{u.segment_count}</td>
                  <td className="px-4 py-3 text-muted-foreground">{u.area_count}</td>
                  <td className="px-4 py-3">
                    {u.global_roles.length > 0
                      ? u.global_roles.join(", ")
                      : <span className="text-muted-foreground">—</span>}
                  </td>
                </tr>
              ))}
              {users.data.items.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">
                    Sin usuarios.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
